import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_ENDPOINTS } from '../../config/api';

const getAuthHeaders = (token, withJson = false) => {
  const headers = { Accept: 'application/json' };
  if (withJson) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const parseValidationError = (result, fallback) => {
  const validationMsg = result?.errors ? Object.values(result.errors).flat().join(' ') : null;
  return validationMsg || result?.message || fallback;
};

export const mapAnnouncementToDisplay = (item) => ({
  id: item.id,
  title: item.title,
  content: item.content,
  type: item.type || 'info',
  target: item.target || 'coupon',
  start_at: item.start_at,
  end_at: item.end_at,
  is_active: Boolean(item.is_active),
  updated_at: item.updated_at,
  created_at: item.created_at,
});

export const fetchAnnouncements = createAsyncThunk(
  'announcement/fetchAnnouncements',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;
      if (!token) return rejectWithValue('認証が必要です');

      let page = 1;
      let lastPage = 1;
      const all = [];

      do {
        const response = await fetch(`${API_ENDPOINTS.ADMIN_ANNOUNCEMENTS}?page=${page}`, {
          method: 'GET',
          headers: getAuthHeaders(token),
        });
        const result = await response.json();
        if (!response.ok) {
          return rejectWithValue(result?.message || 'お知らせ一覧の取得に失敗しました');
        }

        const paginated = result?.data;
        const rows = Array.isArray(paginated?.data) ? paginated.data : [];
        all.push(...rows.map(mapAnnouncementToDisplay));
        lastPage = paginated?.last_page || 1;
        page += 1;
      } while (page <= lastPage);

      return all;
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const fetchActiveAnnouncement = createAsyncThunk(
  'announcement/fetchActiveAnnouncement',
  async (target = 'coupon', { rejectWithValue }) => {
    try {
      const response = await fetch(API_ENDPOINTS.ANNOUNCEMENTS_ACTIVE(target), {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const result = await response.json();
      if (!response.ok) {
        return rejectWithValue(result?.message || 'お知らせの取得に失敗しました');
      }
      const rows = Array.isArray(result?.data)
        ? result.data
        : result?.data
          ? [result.data]
          : [];
      return rows;
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const createAnnouncement = createAsyncThunk(
  'announcement/createAnnouncement',
  async (announcementData, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;
      if (!token) return rejectWithValue('認証が必要です');

      const response = await fetch(API_ENDPOINTS.ADMIN_ANNOUNCEMENTS, {
        method: 'POST',
        headers: getAuthHeaders(token, true),
        body: JSON.stringify(announcementData),
      });
      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(parseValidationError(result, 'お知らせの作成に失敗しました'));
      }

      return mapAnnouncementToDisplay(result?.data ?? {});
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const updateAnnouncement = createAsyncThunk(
  'announcement/updateAnnouncement',
  async ({ id, data }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;
      if (!token) return rejectWithValue('認証が必要です');

      const response = await fetch(API_ENDPOINTS.ADMIN_ANNOUNCEMENT(id), {
        method: 'PUT',
        headers: getAuthHeaders(token, true),
        body: JSON.stringify(data),
      });
      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(parseValidationError(result, 'お知らせの更新に失敗しました'));
      }

      return mapAnnouncementToDisplay(result?.data ?? {});
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const deleteAnnouncement = createAsyncThunk(
  'announcement/deleteAnnouncement',
  async (id, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;
      if (!token) return rejectWithValue('認証が必要です');

      const response = await fetch(API_ENDPOINTS.ADMIN_ANNOUNCEMENT(id), {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      });
      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result?.message || 'お知らせの削除に失敗しました');
      }

      return id;
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

const initialState = {
  announcements: [],
  activeAnnouncement: null,
  currentPage: 1,
  isLoading: false,
  isSubmitting: false,
  isCreateModalOpen: false,
  isEditModalOpen: false,
  editingAnnouncement: null,
  error: null,
};

const announcementSlice = createSlice({
  name: 'announcement',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    openCreateModal: (state) => {
      state.isCreateModalOpen = true;
    },
    closeCreateModal: (state) => {
      state.isCreateModalOpen = false;
    },
    openEditModal: (state, action) => {
      state.isEditModalOpen = true;
      state.editingAnnouncement = action.payload;
    },
    closeEditModal: (state) => {
      state.isEditModalOpen = false;
      state.editingAnnouncement = null;
    },
    clearAnnouncementError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnnouncements.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnnouncements.fulfilled, (state, action) => {
        state.isLoading = false;
        state.announcements = action.payload;
      })
      .addCase(fetchAnnouncements.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchActiveAnnouncement.fulfilled, (state, action) => {
        state.activeAnnouncement = action.payload;
      })
      .addCase(createAnnouncement.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(createAnnouncement.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.isCreateModalOpen = false;
        if (action.payload?.id) {
          state.announcements.unshift(action.payload);
          state.currentPage = 1;
        }
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload;
      })
      .addCase(updateAnnouncement.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(updateAnnouncement.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.isEditModalOpen = false;
        state.editingAnnouncement = null;
        const index = state.announcements.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.announcements[index] = action.payload;
        }
      })
      .addCase(updateAnnouncement.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload;
      })
      .addCase(deleteAnnouncement.fulfilled, (state, action) => {
        state.announcements = state.announcements.filter((a) => a.id !== action.payload);
      });
  },
});

export const {
  setCurrentPage,
  openCreateModal,
  closeCreateModal,
  openEditModal,
  closeEditModal,
  clearAnnouncementError,
} = announcementSlice.actions;

export default announcementSlice.reducer;
